import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  CONSENT_VERSION,
  acceptAll,
  hasDecided,
  pushGtagConsent,
  readConsent,
  rejectAll,
  subscribe,
  writeConsent,
  type ConsentState,
} from "@/lib/consent";
import { bootAnalytics } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

interface ConsentContextValue {
  state: ConsentState;
  hasDecided: boolean;
  /** Banner / modal open state. */
  isOpen: boolean;
  /** Open the per-purpose modal (e.g. from footer "Mes choix cookies"). */
  open: () => void;
  /** Close the modal without changing state. */
  close: () => void;
  /** Accept all non-essential categories. */
  acceptAll: () => void;
  /** Reject all non-essential categories. */
  rejectAll: () => void;
  /** Save granular choices from the modal. Closes the modal. */
  save: (next: { analytics: boolean; ads: boolean; personalization: boolean }) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConsentState>(() => readConsent());
  const [isOpen, setIsOpen] = useState(false);
  // Stable ref to deduplicate user_consents inserts within a session.
  const writtenVersionRef = useRef<string | null>(null);

  // Apply the default-denied posture to gtag on first mount (in addition to
  // the inline bootstrap in index.html), and start the analytics tag loader.
  useEffect(() => {
    pushGtagConsent(state);
    const unsubAnalytics = bootAnalytics();
    const unsubConsent = subscribe(setState);
    return () => {
      unsubAnalytics();
      unsubConsent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the chosen state as a user_consents row when the visitor is
  // authenticated. RLS allows the user to write their own row only.
  // Best-effort: never blocks the UI.
  const recordOnServer = useCallback(async (next: ConsentState) => {
    if (!next.accepted_at) return;
    if (writtenVersionRef.current === next.version) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.from("user_consents").insert({
        user_id: user.id,
        consent_type: "cookies",
        version: next.version,
        user_agent: navigator.userAgent.slice(0, 500),
      });
      writtenVersionRef.current = next.version;
    } catch {
      /* silent — the localStorage copy is the source of truth client-side. */
    }
  }, []);

  const commit = useCallback(
    (next: ConsentState) => {
      writeConsent(next);
      setState(next);
      setIsOpen(false);
      void recordOnServer(next);
    },
    [recordOnServer],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      hasDecided: hasDecided(state),
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      acceptAll: () => commit(acceptAll()),
      rejectAll: () => commit(rejectAll()),
      save: (next) =>
        commit({
          version: CONSENT_VERSION,
          accepted_at: new Date().toISOString(),
          analytics: next.analytics,
          ads: next.ads,
          personalization: next.personalization,
        }),
    }),
    [state, isOpen, commit],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent() must be used inside <ConsentProvider>");
  }
  return ctx;
}
